import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiActivity,
  FiArrowRight,
  FiCalendar,
  FiDollarSign,
  FiMail,
  FiRefreshCw,
  FiUser,
  FiXCircle
} from 'react-icons/fi';
import { accountAPI, historyAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const roleMeta = {
  system: {
    label: 'System Administrator',
    badge: 'border border-white/15 bg-white/10 text-white'
  },
  super_admin: {
    label: 'Super Admin',
    badge: 'border border-white/15 bg-white/10 text-white'
  },
  reseller: {
    label: 'Reseller',
    badge: 'border border-white/15 bg-white/10 text-white'
  },
  user: {
    label: 'User',
    badge: 'border border-white/15 bg-white/10 text-white'
  }
};

const dashboardTheme = {
  gradient: 'from-blue-500 via-blue-600 to-blue-700',
  panel: 'border-blue-100 dark:border-blue-900/30',
  accentBg: 'bg-blue-100 dark:bg-blue-900/30',
  accentText: 'text-blue-700 dark:text-blue-300',
  softBg: 'bg-blue-50 dark:bg-blue-950/20',
  softBorder: 'border-blue-100 dark:border-blue-900/30'
};

const formatInteger = (value) => new Intl.NumberFormat().format(Number(value || 0));

const formatMoney = (value, suffix = '') => {
  const numericValue = Number(value || 0);
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numericValue);
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const toLocalDateTime = (date) => {
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - tzOffset);
  return localDate.toISOString().slice(0, 19);
};

const getTodayRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return {
    from_datetime: toLocalDateTime(start),
    to_datetime: toLocalDateTime(end)
  };
};

const normalizeHistoryRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeSummaryValue = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getAdminCostBreakdown = (costs = {}) => ([
  { label: 'PH', value: normalizeSummaryValue(costs.PH) },
  { label: 'BR', value: normalizeSummaryValue(costs.BR) }
]);

const formatCoinBreakdown = (costs = {}) => (
  getAdminCostBreakdown(costs)
    .map(({ label, value }) => `${formatMoney(value)} ${label}`)
    .join('  |  ')
);

const formatTimeOnly = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const getDisplayName = (row = {}) => (
  row.chat_owner_name ||
  row.username ||
  row.owner_name ||
  row.user_name ||
  row.request_fields?.display_name ||
  row.request_fields?.name ||
  row.customer_name ||
  row.external_order_id ||
  '-'
);

const getGameId = (row = {}) => (
  row.request_fields?.game_id ||
  row.request_fields?.player_id ||
  row.game_id ||
  '-'
);

const getServerId = (row = {}) => (
  row.request_fields?.server_id ||
  row.server_id ||
  '-'
);

const resolveAmountValue = (...candidates) => {
  for (const candidate of candidates) {
    const parsed = parseFloat(candidate);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return 0;
};

const getAmountCandidates = (row = {}) => ({
  price: [
    row.real_amount,
    row.charged_amount,
    row.amount,
    row.price,
    row.user_sale,
    row.total_amount
  ],
  cost: [
    row.real_admin_coin_amount,
    row.admin_coin_amount,
    row.cost,
    row.admin_amount,
    row.coin_price,
    row.reseller_price,
    row.cost_amount,
    row.total_cost
  ]
});

const getRoleMmkAmount = (row = {}, role) => {
  if (role === 'reseller') {
    return resolveAmountValue(
      row.reseller_sale,
      row.real_amount,
      row.charged_amount,
      row.amount,
      row.price
    );
  }

  return resolveAmountValue(
    row.user_sale,
    row.real_amount,
    row.charged_amount,
    row.amount,
    row.price
  );
};

const getPrimaryPriceDisplay = (row = {}, role, currentUserId) => {
  const amountCandidates = getAmountCandidates(row);
  const saleAmount = resolveAmountValue(...amountCandidates.price);
  const saleCurrency = row.charged_currency || row.currency || 'MMK';
  const costAmount = resolveAmountValue(...amountCandidates.cost);
  const costCurrency = row.admin_currency || row.charged_currency || row.currency || 'MMK';
  const isAdmin = role === 'system' || role === 'super_admin';
  const isOwnTransaction = currentUserId && parseInt(row.owner_account_id, 10) === currentUserId;

  if (isAdmin) {
    return {
      amount: saleAmount,
      currency: saleCurrency,
      label: 'Price',
      secondary: !isOwnTransaction
        ? { amount: costAmount, currency: costCurrency, label: 'Cost' }
        : null
    };
  }

  return {
    amount: getRoleMmkAmount(row, role),
    currency: 'MMK',
    label: 'Price',
    secondary: null
  };
};

const getStatusTone = (status) => {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'success') {
    return {
      label: 'Success',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    };
  }

  if (normalized === 'failed') {
    return {
      label: 'Failed',
      className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
    };
  }

  if (normalized === 'refunded') {
    return {
      label: 'Refunded',
      className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
    };
  }

  if (normalized === 'pending') {
    return {
      label: 'Pending',
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    };
  }

  return {
    label: normalized ? normalized.replace(/_/g, ' ') : 'Unknown',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
  };
};

const getGroupStatusMeta = (row = {}) => {
  const total = normalizeSummaryValue(row.total_count);
  const success = normalizeSummaryValue(row.success_count);
  const failed = normalizeSummaryValue(row.failed_count);
  const pending = normalizeSummaryValue(row.pending_count);
  const refunded = normalizeSummaryValue(row.refunded_count);
  const status = String(row.status || '').toLowerCase();

  if (status === 'refunded') return { label: 'Refunded', statusKey: 'refunded' };
  if (status === 'success') return { label: 'Success', statusKey: 'success' };
  if (status === 'failed') return { label: 'Failed', statusKey: 'failed' };
  if (status === 'pending') return { label: 'Pending', statusKey: 'pending' };

  if (total > 0 && refunded === total) return { label: 'Refunded', statusKey: 'refunded' };
  if (total > 0 && success === total) return { label: 'Success', statusKey: 'success' };
  if (total > 0 && pending === total) return { label: 'Pending', statusKey: 'pending' };
  if (total > 0 && failed === total) return { label: 'Failed', statusKey: 'failed' };

  if (success > 0 && (failed > 0 || pending > 0 || refunded > 0)) {
    return { label: 'Partial', statusKey: 'partial' };
  }

  if (pending > 0) return { label: 'Processing', statusKey: 'processing' };
  return { label: 'Unknown', statusKey: status || 'unknown' };
};

const getSummaryStatusCounts = (summary = {}) => {
  const sources = [
    summary?.statuses,
    summary?.statuses_count,
    summary?.status_counts,
    summary?.counts,
    summary?.meta?.statuses,
    summary?.meta?.status_counts,
    summary?.data?.statuses,
    summary?.data?.status_counts
  ].filter(Boolean);

  const combined = {};

  sources.forEach((source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      const normalizedKey = String(key || '').toLowerCase();
      combined[normalizedKey] = normalizeSummaryValue(value);
    });
  });

  return combined;
};

const Dashboard = ({ setCurrentPage }) => {
  const [accountInfo, setAccountInfo] = useState(null);
  const [historySummary, setHistorySummary] = useState(null);
  const [todayRows, setTodayRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { setUser } = useAuth();

  const role = accountInfo?.role || 'user';
  const meta = roleMeta[role] || roleMeta.user;

  const fetchDashboardData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const todayRange = getTodayRange();
      const [accountRes, historyRes, todayListRes] = await Promise.all([
        accountAPI.getMe(),
        historyAPI.checkSummary(todayRange),
        historyAPI.listGroups({
          ...todayRange,
          page: 1,
          page_size: 20
        })
      ]);

      const account = accountRes.data?.success ? accountRes.data.data : null;
      const summary = historyRes.data?.success ? historyRes.data : null;
      const rows = todayListRes.data?.success
        ? normalizeHistoryRows(todayListRes.data.data)
        : [];

      if (account) {
        setAccountInfo(account);
        setUser(account);
      }

      setHistorySummary(summary);
      setTodayRows(rows);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [setUser]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const balances = useMemo(() => {
    const wallet = accountInfo?.balances || {};

    if (role === 'system' || role === 'super_admin') {
      return [
        { label: 'PH Balance', value: formatMoney(wallet.PH || 0) },
        { label: 'BR Balance', value: formatMoney(wallet.BR || 0) }
      ];
    }

    return [
      { label: 'MMK Balance', value: formatMoney(wallet.MMK || 0, 'MMK') }
    ];
  }, [accountInfo, role]);

  const todayMetrics = useMemo(() => {
    const totals = historySummary?.totals || {};
    const metaInfo = historySummary?.meta || {};
    const summaryStatusCounts = getSummaryStatusCounts(historySummary);
    const costBreakdown = totals.costs || {};

    const totalOrders = Number(metaInfo.matched_rows || 0);
    const totalQuantity = Number(metaInfo.matched_quantities || 0);
    const userSale = parseFloat(totals.user_sale || 0);
    const resellerSale = parseFloat(totals.reseller_sale || 0);
    const rowBasedRoleCost = todayRows.reduce(
      (sum, row) => sum + getRoleMmkAmount(row, role),
      0
    );
    const roleCost = (role === 'reseller' ? resellerSale : userSale) || rowBasedRoleCost;
    const listStatusCounts = todayRows.reduce((accumulator, row) => {
      const { statusKey } = getGroupStatusMeta(row);
      accumulator[statusKey] = (accumulator[statusKey] || 0) + 1;
      return accumulator;
    }, {});

    const statusCounts = Object.keys(summaryStatusCounts).length > 0
      ? summaryStatusCounts
      : listStatusCounts;

    const pendingCount =
      (statusCounts.pending || 0) +
      (statusCounts.processing || 0) +
      (statusCounts.partial || 0);
    const successCount = statusCounts.success || 0;
    const failedCount = (statusCounts.failed || 0) + (statusCounts.refunded || 0);

    const resolvedOrderCount =
      totalOrders ||
      successCount +
      failedCount +
      pendingCount;
    const completionRate = resolvedOrderCount > 0 ? (successCount / resolvedOrderCount) * 100 : 0;

    return {
      totalOrders: resolvedOrderCount,
      totalQuantity,
      userSale,
      resellerSale,
      roleCost,
      costBreakdown,
      pendingCount,
      successCount,
      failedCount,
      completionRate
    };
  }, [historySummary, role, todayRows]);

  const tableRows = useMemo(() => {
    const currentUserId = accountInfo?.id;

    return todayRows.slice(0, 12).map((row, index) => {
      const groupStatus = getGroupStatusMeta(row);
      const priceDisplay = getPrimaryPriceDisplay(row, role, currentUserId);

      return {
        id: row.id || row.order_id || row.order_group_id || `${row.created_at || 'row'}-${index}`,
        orderId: row.order_id || row.id || '-',
        createdAt: row.created_at || row.updated_at || row.order_date,
        itemName: row.custom_item_name || row.custom_item_code || '-',
        displayName: getDisplayName(row),
        gameId: getGameId(row),
        serverId: getServerId(row),
        priceDisplay,
        status: groupStatus.statusKey,
        statusLabel: groupStatus.label,
        quantity: row.quantity || row.total_count || row.count || 1,
        ownerAccountId: row.owner_account_id || '-',
        successCount: normalizeSummaryValue(row.success_count),
        pendingCount: normalizeSummaryValue(row.pending_count),
        failedCount: normalizeSummaryValue(row.failed_count),
        refundedCount: normalizeSummaryValue(row.refunded_count)
      };
    });
  }, [accountInfo?.id, role, todayRows]);

  const todayDateLabel = useMemo(() => (
    new Date().toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  ), []);

  const primaryCards = useMemo(() => {
    if (role === 'system' || role === 'super_admin') {
      return [
        {
          title: 'Today Orders',
          value: formatInteger(todayMetrics.totalOrders),
          note: `${formatInteger(todayMetrics.totalQuantity)} quantity matched today`,
          icon: FiActivity,
          tone: 'from-blue-600 to-cyan-500'
        },
        {
          title: 'Today Cost',
          value: formatCoinBreakdown(todayMetrics.costBreakdown),
          note: 'PH and BR coin usage returned by API today',
          icon: FiDollarSign,
          tone: 'from-blue-500 to-blue-700'
        },
        {
          title: 'User Sale',
          value: formatMoney(todayMetrics.userSale, 'MMK'),
          note: 'User-side revenue from today summary',
          icon: FiDollarSign,
          tone: 'from-emerald-600 to-teal-500'
        },
        {
          title: 'Reseller Sale',
          value: formatMoney(todayMetrics.resellerSale, 'MMK'),
          note: 'Reseller-side revenue from today summary',
          icon: FiDollarSign,
          tone: 'from-violet-600 to-indigo-500'
        }
      ];
    }

    return [
      {
        title: 'Today Orders',
        value: formatInteger(todayMetrics.totalOrders),
        note: `${formatInteger(todayMetrics.totalQuantity)} quantity matched today`,
        icon: FiActivity,
        tone: 'from-blue-600 to-cyan-500'
      },
      {
        title: 'Today Cost',
        value: formatMoney(todayMetrics.roleCost, 'MMK'),
        note: 'Your MMK cost from today summary',
        icon: FiDollarSign,
        tone: 'from-blue-500 to-blue-700'
      },
      {
        title: 'Failed / Refunded',
        value: formatInteger(todayMetrics.failedCount),
        note: 'Orders that may need recheck today',
        icon: FiXCircle,
        tone: 'from-rose-600 to-orange-500'
      }
    ];
  }, [role, todayMetrics]);

  const statusItems = useMemo(() => ([
    {
      label: 'Success',
      value: formatInteger(todayMetrics.successCount),
      className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
    },
    {
      label: 'Pending',
      value: formatInteger(todayMetrics.pendingCount),
      className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
    },
    {
      label: 'Failed / Refunded',
      value: formatInteger(todayMetrics.failedCount),
      className: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300'
    }
  ]), [todayMetrics.failedCount, todayMetrics.pendingCount, todayMetrics.successCount]);

  const summaryItems = useMemo(() => {
    const baseItems = [
      { label: 'Matched Orders', value: formatInteger(todayMetrics.totalOrders) },
      { label: 'Matched Quantity', value: formatInteger(todayMetrics.totalQuantity) },
      { label: 'Completion Rate', value: `${todayMetrics.completionRate.toFixed(0)}%` }
    ];

    if (role === 'system' || role === 'super_admin') {
      return [
        ...baseItems,
        { label: 'User Sale', value: formatMoney(todayMetrics.userSale, 'MMK') },
        { label: 'Reseller Sale', value: formatMoney(todayMetrics.resellerSale, 'MMK') }
      ];
    }

    return [
      ...baseItems,
      { label: 'Today Cost', value: formatMoney(todayMetrics.roleCost, 'MMK') }
    ];
  }, [role, todayMetrics]);

  const adminCostItems = useMemo(() => getAdminCostBreakdown(todayMetrics.costBreakdown), [todayMetrics.costBreakdown]);

  const Panel = ({ title, subtitle, action, children, className = '' }) => (
    <section className={`rounded-[28px] border bg-white/95 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur dark:bg-gray-900/90 ${dashboardTheme.panel} ${className}`}>
      <div className="flex flex-col gap-4 px-5 py-5 sm:px-6 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p> : null}
        </div>
        {action ? <div className="w-full md:w-auto">{action}</div> : null}
      </div>
      <div className="px-5 pb-5 sm:px-6 sm:pb-6">{children}</div>
    </section>
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-slate-200 dark:border-slate-800" />
          <div className="absolute inset-0 h-16 w-16 animate-spin rounded-full border-4 border-slate-700 border-t-transparent dark:border-slate-300 dark:border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
      <div className="space-y-6 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.12),transparent_38%)] pb-24 md:pb-6">
      <motion.section
        initial={{ opacity: 0, y: -18 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative overflow-hidden rounded-[32px] bg-gradient-to-br ${dashboardTheme.gradient} p-6 text-white shadow-[0_28px_80px_rgba(15,23,42,0.28)] sm:p-8`}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.16),transparent_28%),linear-gradient(135deg,rgba(255,255,255,0.06),transparent_50%)]" />
        <div className="relative grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
          <div className="space-y-5">
            <span className={`inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${meta.badge}`}>
              {meta.label}
            </span>
            <div className="space-y-3">
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl xl:text-[2.8rem]">
                Hello, {accountInfo?.account_name || 'Admin'}
              </h1>
              <p className="max-w-2xl text-sm leading-7 text-blue-50/90 sm:text-base">
                A focused overview of today's order activity, balances, and performance.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-blue-50/90">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-2">
                <FiCalendar className="h-4 w-4" />
                {todayDateLabel}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-2">
                <FiMail className="h-4 w-4" />
                {accountInfo?.email || '-'}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-2">
                <FiUser className="h-4 w-4" />
                {typeof accountInfo?.telegram === 'object'
                  ? `@${accountInfo.telegram?.user_name || 'Not linked'}`
                  : accountInfo?.telegram || 'Not linked'}
              </span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
            {balances.map((balance) => (
              <div
                key={balance.label}
                className="rounded-[26px] border border-white/10 bg-white/10 p-5 backdrop-blur-md"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-blue-50/80">{balance.label}</p>
                    <p className="mt-2 text-2xl font-black text-white">{balance.value}</p>
                    <p className="mt-2 text-xs text-blue-50/70">Current available balance</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/12">
                    <FiDollarSign className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      <section className={`grid gap-4 ${role === 'system' || role === 'super_admin' ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3'}`}>
        {primaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group relative overflow-hidden rounded-[26px] border border-blue-100 bg-white p-5 shadow-[0_16px_40px_rgba(37,99,235,0.10)] transition-transform duration-300 hover:-translate-y-1 dark:border-blue-900/30 dark:bg-gray-900"
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${card.tone}`} />
              <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-slate-100/80 blur-2xl transition-opacity duration-300 group-hover:opacity-80 dark:bg-slate-800/60" />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{card.title}</p>
                  <p className="mt-2 break-words text-2xl font-black leading-tight text-gray-900 dark:text-white">{card.value}</p>
                  <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{card.note}</p>
                </div>
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${card.tone} text-white shadow-lg`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.75fr)]">
        <Panel
          title="Recent Activity"
          subtitle="Latest grouped records returned for today's dashboard window."
          action={(
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => fetchDashboardData(true)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900/30 dark:bg-blue-950/20 dark:text-blue-300 sm:w-auto"
              >
                <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage?.('history')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-blue-600 bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:from-blue-600 hover:to-blue-700 dark:border-blue-400 dark:from-blue-500 dark:to-blue-600 sm:w-auto"
              >
                Open History
                <FiArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        >
          {tableRows.length > 0 ? (
            <>
              <div className={`hidden overflow-hidden rounded-[24px] border md:block ${dashboardTheme.softBorder}`}>
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-blue-100 dark:divide-blue-900/30">
                  <thead className={`${dashboardTheme.softBg}`}>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Order ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Package</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Display Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Game ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Server ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Time</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50 bg-white dark:divide-blue-900/20 dark:bg-gray-900">
                    {tableRows.map((row) => {
                      const statusTone = getStatusTone(row.status);
                      return (
                        <tr key={row.id} className="transition-colors hover:bg-blue-50/60 dark:hover:bg-blue-950/10">
                          <td className="px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">#{row.orderId}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{row.itemName}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{row.displayName}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{row.gameId}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{row.serverId}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{formatInteger(row.quantity)}</td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone.className}`}>
                              {row.statusLabel || statusTone.label}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{formatTimeOnly(row.createdAt)}</td>
                          <td className="px-4 py-4 text-right">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {formatMoney(row.priceDisplay.amount)} {row.priceDisplay.currency}
                            </p>
                            {row.priceDisplay.secondary ? (
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {row.priceDisplay.secondary.label}: {formatMoney(row.priceDisplay.secondary.amount)} {row.priceDisplay.secondary.currency}
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
              <div className="space-y-3 md:hidden">
                {tableRows.map((row) => {
                  const statusTone = getStatusTone(row.status);
                  return (
                    <article
                      key={row.id}
                      className={`rounded-[24px] border bg-white p-4 shadow-sm dark:bg-slate-950 ${dashboardTheme.softBorder}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">Order #{row.orderId}</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {formatTimeOnly(row.createdAt)}
                          </p>
                        </div>
                        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone.className}`}>
                          {row.statusLabel || statusTone.label}
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div className={`rounded-2xl ${dashboardTheme.softBg} p-3`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Package</p>
                          <p className="mt-1 break-words font-medium text-slate-900 dark:text-white">{row.itemName}</p>
                        </div>
                        <div className={`rounded-2xl ${dashboardTheme.softBg} p-3`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Display Name</p>
                          <p className="mt-1 font-medium text-slate-900 dark:text-white">{row.displayName}</p>
                        </div>
                        <div className={`rounded-2xl ${dashboardTheme.softBg} p-3`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Game ID</p>
                          <p className="mt-1 font-semibold text-slate-900 dark:text-white">{row.gameId}</p>
                        </div>
                        <div className={`rounded-2xl ${dashboardTheme.softBg} p-3`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Server ID</p>
                          <p className="mt-1 font-semibold text-slate-900 dark:text-white">{row.serverId}</p>
                        </div>
                        <div className={`rounded-2xl ${dashboardTheme.softBg} p-3`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Quantity</p>
                          <p className="mt-1 font-semibold text-slate-900 dark:text-white">{formatInteger(row.quantity)}</p>
                        </div>
                        <div className={`rounded-2xl ${dashboardTheme.softBg} p-3 col-span-2`}>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Price</p>
                          <p className="mt-1 font-semibold text-slate-900 dark:text-white">
                            {formatMoney(row.priceDisplay.amount)} {row.priceDisplay.currency}
                          </p>
                          {row.priceDisplay.secondary ? (
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {row.priceDisplay.secondary.label}: {formatMoney(row.priceDisplay.secondary.amount)} {row.priceDisplay.secondary.currency}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      {(row.successCount || row.pendingCount || row.failedCount || row.refundedCount) ? (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                          {row.successCount ? (
                            <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              Success {formatInteger(row.successCount)}
                            </span>
                          ) : null}
                          {row.pendingCount ? (
                            <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                              Pending {formatInteger(row.pendingCount)}
                            </span>
                          ) : null}
                          {row.failedCount ? (
                            <span className="rounded-full bg-rose-100 px-2.5 py-1 font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                              Failed {formatInteger(row.failedCount)}
                            </span>
                          ) : null}
                          {row.refundedCount ? (
                            <span className="rounded-full bg-violet-100 px-2.5 py-1 font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                              Refunded {formatInteger(row.refundedCount)}
                            </span>
                          ) : null}
                        </div>
                      ) : null}

                      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                        Owner ID: {row.ownerAccountId}
                      </p>
                    </article>
                  );
                })}
              </div>
            </>
          ) : (
            <div className={`rounded-[24px] border border-dashed px-5 py-12 text-center ${dashboardTheme.softBorder}`}>
              <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${dashboardTheme.accentBg}`}>
                <FiCalendar className={`h-6 w-6 ${dashboardTheme.accentText}`} />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">No activity yet for today</h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Today's order records will appear here as soon as the API returns them.
              </p>
            </div>
          )}
        </Panel>

        <div className="space-y-6">
          <Panel
            title="Today's Snapshot"
            subtitle="Key status distribution from today's summary check."
          >
            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
              {statusItems.map((item) => (
                <div
                  key={item.label}
                  className={`rounded-[22px] px-4 py-4 shadow-sm ${item.className}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] opacity-75">{item.label}</p>
                  <p className="mt-2 text-3xl font-black">{item.value}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel
            title="Overview"
            subtitle="Role-based figures selected from today's account and order data."
          >
            <div className="space-y-3">
              {summaryItems.map((item) => (
                <div key={item.label} className={`rounded-2xl border p-4 ${dashboardTheme.softBg} ${dashboardTheme.softBorder}`}>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{item.label}</p>
                  <p className="mt-2 text-2xl font-black text-slate-900 dark:text-white">{item.value}</p>
                </div>
              ))}
              {(role === 'system' || role === 'super_admin') ? (
                <div className={`rounded-2xl border p-4 ${dashboardTheme.softBg} ${dashboardTheme.softBorder}`}>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Today Cost By Coin</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {adminCostItems.map((item) => (
                      <div key={item.label} className="rounded-2xl bg-white px-4 py-3 shadow-sm dark:bg-slate-950">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{item.label}</p>
                        <p className="mt-2 text-xl font-black text-slate-900 dark:text-white">{formatMoney(item.value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </Panel>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
